/**
 * BannerSection Components
 * Hero full-width, carrusel de 3 columnas y fila de 2 columnas para publicidad
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../services/api';

// ==================== HERO BANNER (full-width with gradient fade) ====================
export const HeroBanner = ({ banners = [], children }) => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % banners.length);
  }, [banners.length]);

  // Auto-cycle every 6 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [banners.length, next]);

  if (!banners || banners.length === 0) {
    // Fallback: dark blue hero with decorative circles
    return (
      <div className="relative bg-[#0f172a] overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-1/4 w-40 h-40 rounded-full bg-white/[0.03]" />
        <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
          {children}
        </div>
      </div>
    );
  }

  const banner = banners[current];

  const handleClick = () => {
    if (banner.link_url) {
      if (banner.link_url.startsWith('http')) {
        window.open(banner.link_url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = banner.link_url;
      }
    }
  };

  return (
    <div className="relative">
      {/* Banner image area */}
      <div
        className={`relative w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[460px] overflow-hidden ${banner.link_url ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
      >
        {banners.map((b, idx) => (
          <div
            key={b.id || idx}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: idx === current ? 1 : 0 }}
          >
            {b.image_url ? (
              <img
                src={b.image_url}
                alt={b.title || 'Banner'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#0f172a]" />
            )}
          </div>
        ))}

        {/* Text overlay */}
        {(banner.overlay_title || banner.overlay_subtitle) && (
          <div className="absolute inset-0 flex items-center z-10">
            <div className="max-w-7xl mx-auto px-4 w-full">
              <div style={{ maxWidth: `${banner.text_max_width || 50}%` }}
                className={`${banner.text_position === 'center' ? 'mx-auto text-center' : banner.text_position === 'right' ? 'ml-auto text-right' : ''}`}
              >
                {banner.overlay_title && (
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg"
                    style={{ color: banner.text_color || '#fff' }}
                  >
                    {banner.overlay_title}
                  </h2>
                )}
                {banner.overlay_subtitle && (
                  <p className="mt-2 text-base sm:text-lg md:text-xl drop-shadow-md"
                    style={{ color: banner.text_color || '#fff', opacity: 0.9 }}
                  >
                    {banner.overlay_subtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Gradient fade to gray background */}
        <div className="absolute bottom-0 left-0 right-0 h-[35%] bg-gradient-to-t from-gray-100 to-transparent pointer-events-none" />

        {/* Navigation arrows */}
        {banners.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setCurrent(prev => (prev - 1 + banners.length) % banners.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </>
        )}
      </div>

      {/* Search bar overlay - floats at the bottom of the hero */}
      {children && (
        <div className="relative z-10 -mt-10 sm:-mt-12">
          <div className="max-w-2xl mx-auto px-4">
            {children}
          </div>
        </div>
      )}

      {/* Dots */}
      {banners.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {banners.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`h-1.5 rounded-full transition-all ${idx === current ? 'w-6 bg-slate-800' : 'w-1.5 bg-slate-300'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BANNER CAROUSEL (3 columns) ====================
export const BannerCarousel = ({ banners = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  if (!banners || banners.length === 0) return null;
  
  // Group banners into slides of 3
  const slidesCount = Math.ceil(banners.length / 3);
  const slides = [];
  for (let i = 0; i < slidesCount; i++) {
    slides.push(banners.slice(i * 3, i * 3 + 3));
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative" data-testid="banner-carousel">
      {/* Navigation arrows - only show if more than 1 slide */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </>
      )}

      {/* Slides container */}
      <div className="overflow-hidden">
        <div 
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slideBanners, slideIndex) => (
            <div 
              key={slideIndex} 
              className="w-full flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {slideBanners.map((banner, bannerIndex) => (
                <BannerCard key={banner.id || bannerIndex} banner={banner} />
              ))}
              {/* Fill empty slots with placeholders if needed */}
              {slideBanners.length < 3 && Array(3 - slideBanners.length).fill(null).map((_, i) => (
                <div key={`placeholder-${i}`} className="hidden lg:block" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Dots indicator */}
      {slides.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentSlide === index ? 'bg-slate-800' : 'bg-slate-300'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BANNER ROW (2 columns) ====================
export const BannerRow = ({ banners = [] }) => {
  if (!banners || banners.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="banner-row">
      {banners.slice(0, 2).map((banner, index) => (
        <BannerCard key={banner.id || index} banner={banner} large />
      ))}
    </div>
  );
};

// ==================== BANNER CARD ====================
const BannerCard = ({ banner, large = false }) => {
  const handleClick = () => {
    if (banner.link_url) {
      if (banner.link_url.startsWith('http')) {
        window.open(banner.link_url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = banner.link_url;
      }
    }
  };

  const hasOverlay = banner.overlay_title || banner.overlay_subtitle;

  // Box position within the banner
  const boxJustify = banner.text_position === 'center' ? 'justify-center'
    : banner.text_position === 'right' ? 'justify-end'
    : 'justify-start';

  // Text alignment inside the box
  const innerAlign = banner.text_align === 'center' ? 'text-center'
    : banner.text_align === 'right' ? 'text-right'
    : 'text-left';

  const titleSizeMap = { sm: 'text-sm', md: 'text-base md:text-lg', lg: 'text-lg md:text-xl', xl: 'text-xl md:text-2xl', '2xl': 'text-2xl md:text-3xl' };
  const subtitleSizeMap = { xs: 'text-xs', sm: 'text-xs md:text-sm', md: 'text-sm md:text-base', lg: 'text-base md:text-lg', xl: 'text-lg md:text-xl' };
  const titleClass = titleSizeMap[banner.title_size] || titleSizeMap['md'];
  const subtitleClass = subtitleSizeMap[banner.subtitle_size] || subtitleSizeMap['sm'];

  return (
    <div
      onClick={handleClick}
      className={`relative overflow-hidden rounded-lg ${
        banner.link_url ? 'cursor-pointer' : ''
      } group`}
      style={{ aspectRatio: large ? '2.4/1' : '2/1' }}
      data-testid={`banner-card-${banner.id}`}
    >
      {banner.image_url ? (
        <img
          src={banner.image_url}
          alt={banner.title || 'Banner publicitario'}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-r from-slate-200 to-slate-300 flex items-center justify-center">
          <span className="text-slate-500 text-sm">{banner.title || 'Banner'}</span>
        </div>
      )}

      {/* Text overlay */}
      {hasOverlay && (
        <div className={`absolute inset-0 flex ${boxJustify} items-center px-4`}>
          <div className={`flex flex-col ${innerAlign} backdrop-blur-sm rounded-lg px-4 py-3`}
            style={{
              maxWidth: `${banner.text_max_width || 100}%`,
              backgroundColor: 'rgba(0,0,0,0.45)'
            }}
          >
            {banner.overlay_title && (
              <h3
                className={`font-bold ${titleClass}`}
                style={{ color: banner.text_color || '#ffffff' }}
              >
                {banner.overlay_title}
              </h3>
            )}
            {banner.overlay_subtitle && (
              <p
                className={`mt-1 ${subtitleClass}`}
                style={{ color: banner.text_color || '#ffffff', opacity: 0.9 }}
              >
                {banner.overlay_subtitle}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Hover overlay for clickable banners */}
      {banner.link_url && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      )}
    </div>
  );
};

// ==================== BANNER SECTION (Combined) ====================
export const BannerSection = () => {
  const [banners, setBanners] = useState({ carousel: [], row: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response = await api.get('/banners/public');
        setBanners(response.data);
      } catch (error) {
        console.error('Error loading banners:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, []);

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Skeleton for carousel */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-slate-200 rounded-xl animate-pulse" style={{ aspectRatio: '2/1' }} />
          ))}
        </div>
        {/* Skeleton for row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-slate-200 rounded-xl animate-pulse" style={{ aspectRatio: '2.4/1' }} />
          ))}
        </div>
      </div>
    );
  }

  // Don't render if no banners
  if (banners.carousel.length === 0 && banners.row.length === 0) {
    return null;
  }

  return (
    <div className="space-y-8">
      {banners.carousel.length > 0 && (
        <BannerCarousel banners={banners.carousel} />
      )}
      {banners.row.length > 0 && (
        <BannerRow banners={banners.row} />
      )}
    </div>
  );
};

export default BannerSection;
